import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import DateInputWithArrows from '../../components/DateInputWithArrows';
import { UNITS } from '../../constants/units';
import {
  createWarehouseInbound,
  getCompanies,
  getFinanceDocuments,
  getWarehouseInboundUploadUrl,
} from '../../services/api';

const CATEGORIES = ['Material', 'Equipment', 'Machine', 'Tool'];
const TOOL_TYPES = ['HAND_TOOL', 'ELECTRIC_TOOL', 'MEASURING_EQUIPMENT', 'CONSTRUCTION_TOOL', 'AUXILIARY_TOOL', 'OTHER'];

function emptyItem() {
  return {
    category: 'Material',
    name: '',
    unit: UNITS.UNIT,
    quantity: '',
    unitPrice: '',
    serialNumber: '',
    toolType: 'OTHER',
  };
}

async function uploadFile(file) {
  if (!file) return null;
  const presign = await getWarehouseInboundUploadUrl({
    originalName: file.name,
    contentType: file.type || 'application/octet-stream',
  });
  await fetch(presign.data.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  return {
    objectKey: presign.data.objectKey,
    originalName: file.name,
    mimeType: file.type || 'application/octet-stream',
    size: file.size || 0,
  };
}

export default function WarehouseInboundDialog({ open, onClose, onSaved }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState('OPENING');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [items, setItems] = useState([emptyItem()]);
  const [financeDoc, setFinanceDoc] = useState('DELIVERY_NOTE');
  const [companies, setCompanies] = useState([]);
  const [partnerName, setPartnerName] = useState('');
  const [companyId, setCompanyId] = useState(null);
  const [deliveryNoteNumber, setDeliveryNoteNumber] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [deliveryNoteFile, setDeliveryNoteFile] = useState(null);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [paidAtRegister, setPaidAtRegister] = useState(false);
  const [existingNote, setExistingNote] = useState(null);
  const [noteOptions, setNoteOptions] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [reuseDocs, setReuseDocs] = useState({});
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const wantsDelivery = financeDoc === 'DELIVERY_NOTE' || financeDoc === 'BOTH';
  const wantsInvoice = financeDoc === 'INVOICE' || financeDoc === 'BOTH';

  useEffect(() => {
    if (!open) return;
    getCompanies().then((res) => setCompanies(res.data || [])).catch(() => setCompanies([]));
    getFinanceDocuments({ type: 'DELIVERY_NOTE' }).then((res) => setNoteOptions(res.data || [])).catch(() => setNoteOptions([]));
  }, [open]);

  const itemsValid = items.every((item) => {
    if (!item.name) return false;
    if (item.category === 'Tool') return Boolean(item.serialNumber);
    return Number(item.quantity) > 0;
  });

  const submit = async (extra = {}) => {
    setSaving(true);
    setError('');
    try {
      const deliveryNoteAttachment = mode === 'PURCHASE' && wantsDelivery
        ? await uploadFile(deliveryNoteFile)
        : null;
      const invoiceAttachment = mode === 'PURCHASE' && wantsInvoice
        ? await uploadFile(invoiceFile)
        : null;
      await createWarehouseInbound({
        mode,
        deliveryDate: date,
        items: items.map((item) => ({
          category: item.category,
          name: item.name,
          unit: item.unit,
          quantity: item.category === 'Tool' ? 1 : Number(item.quantity),
          unitPrice: item.unitPrice === '' ? undefined : Number(item.unitPrice),
          serialNumber: item.serialNumber || undefined,
          toolType: item.toolType,
        })),
        financeDocuments: mode === 'PURCHASE'
          ? (financeDoc === 'BOTH' ? ['DELIVERY_NOTE', 'INVOICE'] : [financeDoc])
          : [],
        companyId: companyId || undefined,
        counterpartyName: partnerName || undefined,
        deliveryNoteNumber: deliveryNoteNumber || undefined,
        invoiceNumber: invoiceNumber || undefined,
        deliveryNoteAttachment: deliveryNoteAttachment || undefined,
        invoiceAttachment: invoiceAttachment || undefined,
        paidAtRegister: Boolean(paidAtRegister),
        confirmDuplicate: Boolean(extra.confirmDuplicate),
        existingDeliveryNoteId: extra.confirmDuplicate
          ? undefined
          : (extra.existingDeliveryNoteId || reuseDocs.existingDeliveryNoteId || existingNote?.id || undefined),
        existingInvoiceId: extra.confirmDuplicate
          ? undefined
          : (extra.existingInvoiceId || reuseDocs.existingInvoiceId || undefined),
      });
      onSaved();
      onClose();
    } catch (err) {
      if (err?.response?.data?.code === 'DUPLICATE') {
        setDuplicates(err.response.data.duplicates || []);
        setError(t('siteDelivery.duplicateWarning'));
      } else {
        setError(err?.response?.data?.error || t('warehouse.inboundFailed'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{t('warehouse.inboundTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity={duplicates.length ? 'warning' : 'error'}>{error}</Alert>}
          {duplicates.map((dup) => (
            <Alert key={dup.id} severity="info">
              {t(`finance.types.${dup.type}`)} {dup.documentNumber} — {dup.counterpartyName}
              <Button
                size="small"
                sx={{ ml: 1 }}
                onClick={() => {
                  const next = dup.type === 'INVOICE'
                    ? { ...reuseDocs, existingInvoiceId: dup.id }
                    : { ...reuseDocs, existingDeliveryNoteId: dup.id };
                  setReuseDocs(next);
                  submit(next);
                }}
              >
                {t('siteDelivery.useExisting')}
              </Button>
            </Alert>
          ))}
          {duplicates.length > 0 && (
            <Button size="small" onClick={() => submit({ confirmDuplicate: true })}>{t('siteDelivery.saveAnyway')}</Button>
          )}
          <FormControl fullWidth>
            <InputLabel>{t('warehouse.inboundMode')}</InputLabel>
            <Select label={t('warehouse.inboundMode')} value={mode} onChange={(e) => setMode(e.target.value)}>
              <MenuItem value="OPENING">{t('warehouse.inboundOpening')}</MenuItem>
              <MenuItem value="PURCHASE">{t('warehouse.inboundPurchase')}</MenuItem>
            </Select>
          </FormControl>
          <Alert severity="info">
            {mode === 'OPENING' ? t('warehouse.inboundOpeningHint') : t('warehouse.inboundPurchaseHint')}
          </Alert>
          <DateInputWithArrows label={t('siteDelivery.date')} value={date} onChange={(e) => setDate(e.target.value)} />
          {mode === 'PURCHASE' && (
            <>
              <FormControl fullWidth>
                <InputLabel>{t('siteDelivery.financeDoc')}</InputLabel>
                <Select label={t('siteDelivery.financeDoc')} value={financeDoc} onChange={(e) => setFinanceDoc(e.target.value)}>
                  <MenuItem value="DELIVERY_NOTE">{t('siteDelivery.financeDelivery')}</MenuItem>
                  <MenuItem value="INVOICE">{t('siteDelivery.financeInvoice')}</MenuItem>
                  <MenuItem value="BOTH">{t('siteDelivery.financeBoth')}</MenuItem>
                </Select>
              </FormControl>
              {wantsDelivery && (
                <Autocomplete
                  options={noteOptions}
                  getOptionLabel={(opt) => `${opt.documentNumber || `#${opt.id}`} · ${opt.counterpartyName}`}
                  value={existingNote}
                  onChange={(_, value) => {
                    setExistingNote(value);
                    if (value) {
                      setPartnerName(value.counterpartyName || partnerName);
                      setCompanyId(value.companyId || companyId);
                      setDeliveryNoteNumber(value.documentNumber || deliveryNoteNumber);
                    }
                  }}
                  onInputChange={(_, value) => {
                    getFinanceDocuments({ type: 'DELIVERY_NOTE', search: value || undefined })
                      .then((res) => setNoteOptions(res.data || []))
                      .catch(() => setNoteOptions([]));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label={t('siteDelivery.findExistingNote')} helperText={t('siteDelivery.findExistingNoteHint')} />
                  )}
                />
              )}
              <Autocomplete
                options={companies}
                getOptionLabel={(opt) => opt.name || ''}
                onChange={(_, value) => { setCompanyId(value?.id || null); setPartnerName(value?.name || partnerName); }}
                inputValue={partnerName}
                onInputChange={(_, value) => setPartnerName(value)}
                freeSolo
                renderInput={(params) => <TextField {...params} label={t('siteDelivery.partner')} />}
              />
              {wantsDelivery && (
                <>
                  <TextField label={t('siteDelivery.deliveryNoteNumber')} value={deliveryNoteNumber} onChange={(e) => setDeliveryNoteNumber(e.target.value)} />
                  <Button component="label" variant="outlined">
                    {deliveryNoteFile ? deliveryNoteFile.name : t('siteDelivery.deliveryNoteAttachment')}
                    <input hidden type="file" accept=".pdf,image/*" onChange={(e) => setDeliveryNoteFile(e.target.files?.[0] || null)} />
                  </Button>
                </>
              )}
              {wantsInvoice && (
                <>
                  <TextField label={t('siteDelivery.invoiceNumber')} value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} />
                  <Button component="label" variant="outlined">
                    {invoiceFile ? invoiceFile.name : t('siteDelivery.invoiceAttachment')}
                    <input hidden type="file" accept=".pdf,image/*" onChange={(e) => setInvoiceFile(e.target.files?.[0] || null)} />
                  </Button>
                  <FormControlLabel control={<Checkbox checked={paidAtRegister} onChange={(e) => setPaidAtRegister(e.target.checked)} />} label={t('siteDelivery.paidAtRegister')} />
                </>
              )}
            </>
          )}
          <Typography variant="subtitle2">{t('siteDelivery.items')}</Typography>
          {items.map((item, idx) => (
            <Grid container spacing={1} key={idx} alignItems="center">
              <Grid item xs={12} md={2}>
                <Select fullWidth value={item.category} onChange={(e) => {
                  const next = [...items];
                  next[idx] = { ...next[idx], category: e.target.value };
                  setItems(next);
                }}>
                  {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{t(`warehouse.itemCategory.${c}`)}</MenuItem>)}
                </Select>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField fullWidth label={t('siteDelivery.materialName')} value={item.name} onChange={(e) => {
                  const next = [...items];
                  next[idx] = { ...next[idx], name: e.target.value };
                  setItems(next);
                }} />
              </Grid>
              {item.category === 'Tool' ? (
                <>
                  <Grid item xs={6} md={2}>
                    <TextField fullWidth label={t('tools.fields.serialNumber')} value={item.serialNumber} onChange={(e) => {
                      const next = [...items];
                      next[idx] = { ...next[idx], serialNumber: e.target.value };
                      setItems(next);
                    }} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Select fullWidth value={item.toolType} onChange={(e) => {
                      const next = [...items];
                      next[idx] = { ...next[idx], toolType: e.target.value };
                      setItems(next);
                    }}>
                      {TOOL_TYPES.map((tp) => <MenuItem key={tp} value={tp}>{tp}</MenuItem>)}
                    </Select>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={6} md={2}>
                    <TextField fullWidth type="number" label={t('siteDelivery.amount')} value={item.quantity} onChange={(e) => {
                      const next = [...items];
                      next[idx] = { ...next[idx], quantity: e.target.value };
                      setItems(next);
                    }} />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <FormControl fullWidth>
                      <InputLabel>{t('finance.unit')}</InputLabel>
                      <Select
                        label={t('finance.unit')}
                        value={item.unit}
                        onChange={(e) => {
                          const next = [...items];
                          next[idx] = { ...next[idx], unit: e.target.value };
                          setItems(next);
                        }}
                      >
                        {Object.values(UNITS).concat(item.unit && !Object.values(UNITS).includes(item.unit) ? [item.unit] : []).map((unit) => (
                          <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}
              <Grid item xs={2} md={1}>
                <IconButton onClick={() => setItems(items.filter((_, i) => i !== idx))} disabled={items.length === 1}><DeleteIcon /></IconButton>
              </Grid>
            </Grid>
          ))}
          <Button onClick={() => setItems([...items, emptyItem()])}>{t('siteDelivery.addItem')}</Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          disabled={saving || !itemsValid || (mode === 'PURCHASE' && (!partnerName || (wantsInvoice && !invoiceFile && !existingNote)))}
          onClick={() => submit()}
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
